import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Clock, BarChart3, Users, CheckCircle, ArrowRight, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function LandingPage() {
    const [searchParams] = useSearchParams();
    const source = searchParams.get('source');
    const isRegistrationEnabled = source === 'lgs';

    const RegistrationButton = ({ className, variant = "default", size = "default", children }) => {
        if (isRegistrationEnabled) {
            return (
                <Link to={`/register?source=${source}`}>
                    <Button className={className} variant={variant} size={size}>
                        {children}
                    </Button>
                </Link>
            );
        }

        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span tabIndex={0}>
                            <Button className={className} variant={variant} size={size} disabled>
                                {children}
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>New onboarding is currently on pause</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    return (
        <div className="min-h-screen bg-[#09090B] relative overflow-hidden flex flex-col">
            {/* Background patterns */}
            <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-cyan-500/20 blur-[120px] rounded-full pointer-events-none" />

            {/* Navbar */}
            <nav className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 text-black" />
                        </div>
                        <span className="text-lg font-bold text-white font-['Manrope']">InSynq</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/blog">
                            <Button variant="ghost" className="text-zinc-400 hover:text-white">Blog</Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" className="text-zinc-400 hover:text-white">Sign In</Button>
                        </Link>
                        <RegistrationButton className="bg-white text-black hover:bg-zinc-200">
                            Get Started
                        </RegistrationButton>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="flex-1 relative z-10">
                <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                        </span>
                        New: Smart Time Analytics
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight font-['Manrope'] animate-in fade-in slide-in-from-bottom-8 duration-700">
                        Track time without <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
                            the distraction
                        </span>
                    </h1>

                    <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10 animate-in fade-in slide-in-from-bottom-12 duration-1000">
                        The minimal time tracking tool designed for high-performance teams.
                        Focus on your work, not the clock.
                    </p>

                    <div className="flex items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-16 duration-1000 delay-200">
                        <RegistrationButton size="lg" className="h-12 px-8 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold text-base transition-all hover:scale-105">
                            Start Tracking Free <ArrowRight className="w-4 h-4 ml-2" />
                        </RegistrationButton>
                        <Link to="/login">
                            <Button size="lg" variant="outline" className="h-12 px-8 border-zinc-700 text-zinc-300 hover:text-white hover:bg-white/5 transition-all">
                                Live Demo
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Features Grid */}
                <div className="max-w-7xl mx-auto px-6 py-24">
                    <div className="grid md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={<Clock className="w-6 h-6 text-cyan-400" />}
                            title="Real-time Tracking"
                            description="Seamlessly track time across tasks and projects with our unobtrusive timer widget."
                            delay="0"
                        />
                        <FeatureCard
                            icon={<BarChart3 className="w-6 h-6 text-blue-400" />}
                            title="Instant Analytics"
                            description="Get powerful insights into where your team's time goes without complex reports."
                            delay="100"
                        />
                        <FeatureCard
                            icon={<Users className="w-6 h-6 text-purple-400" />}
                            title="Team Collaboration"
                            description="Keep everyone in sync with real-time activity logs and shared project timelines."
                            delay="200"
                        />
                    </div>
                </div>
            </main>

            {/* Pricing Section */}
            <div className="max-w-7xl mx-auto px-6 pb-24">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-white mb-4 font-['Manrope']">Simple, transparent pricing</h2>
                    <p className="text-zinc-400">Choose the plan that's right for your team.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    {/* Free Tier */}
                    <div className="p-8 rounded-2xl bg-zinc-900/50 border border-white/5 flex flex-col">
                        <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">Starter</h3>
                        <div className="mb-6">
                            <span className="text-4xl font-bold text-white">$0</span>
                            <span className="text-zinc-500">/month</span>
                        </div>
                        <p className="text-zinc-400 mb-8 min-h-[48px]">Perfect for freelancers and individuals just getting started.</p>
                        <div className="space-y-4 mb-8 flex-1">
                            <PricingFeature>Up to 3 projects</PricingFeature>
                            <PricingFeature>Basic analytics</PricingFeature>
                            <PricingFeature>7-day history</PricingFeature>
                            <PricingFeature>1 user</PricingFeature>
                        </div>
                        <RegistrationButton className="w-full bg-white/10 hover:bg-white/20 text-white" variant="outline">
                            Get Started
                        </RegistrationButton>
                    </div>

                    {/* Pro Tier */}
                    <div className="p-8 rounded-2xl bg-zinc-900/80 border border-cyan-500/50 flex flex-col relative">
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-cyan-500 text-black text-xs font-bold rounded-full uppercase tracking-wide">
                            Most Popular
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">Pro</h3>
                        <div className="mb-6">
                            <span className="text-4xl font-bold text-white">$12</span>
                            <span className="text-zinc-500">/user/mo</span>
                        </div>
                        <p className="text-zinc-400 mb-8 min-h-[48px]">For growing teams that need more power and flexibility.</p>
                        <div className="space-y-4 mb-8 flex-1">
                            <PricingFeature checked>Unlimited projects</PricingFeature>
                            <PricingFeature checked>Advanced analytics</PricingFeature>
                            <PricingFeature checked>Unlimited history</PricingFeature>
                            <PricingFeature checked>Priority support</PricingFeature>
                            <PricingFeature checked>Export reports</PricingFeature>
                        </div>
                        <RegistrationButton className="w-full bg-cyan-500 hover:bg-cyan-400 text-black">
                            Start Free Trial
                        </RegistrationButton>
                    </div>

                    {/* Enterprise Tier */}
                    <div className="p-8 rounded-2xl bg-zinc-900/50 border border-white/5 flex flex-col">
                        <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">Business</h3>
                        <div className="mb-6">
                            <span className="text-4xl font-bold text-white">$25</span>
                            <span className="text-zinc-500">/user/mo</span>
                        </div>
                        <p className="text-zinc-400 mb-8 min-h-[48px]">Advanced controls and support for large organizations.</p>
                        <div className="space-y-4 mb-8 flex-1">
                            <PricingFeature>Everything in Pro</PricingFeature>
                            <PricingFeature>SSO / SAML</PricingFeature>
                            <PricingFeature>Audit logs</PricingFeature>
                            <PricingFeature>Dedicated account manager</PricingFeature>
                            <PricingFeature>SLA guarantee</PricingFeature>
                        </div>
                        <Button className="w-full bg-white/10 hover:bg-white/20 text-white" variant="outline">
                            Contact Sales
                        </Button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="relative z-10 border-t border-white/5 py-12 bg-black/20">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2 text-zinc-400">
                        <div className="w-6 h-6 bg-zinc-800 rounded flex items-center justify-center">
                            <Clock className="w-3 h-3 text-zinc-400" />
                        </div>
                        <span className="font-semibold text-zinc-200">InSynq</span>
                        <span>© 2025</span>
                    </div>
                    <div className="flex gap-8 text-sm text-zinc-500">
                        <Link to="/blog" className="hover:text-zinc-300 transition-colors">Blog</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, description, delay }) {
    return (
        <div
            className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-cyan-500/20 hover:bg-zinc-900/80 transition-all duration-300 group"
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="w-12 h-12 rounded-xl bg-zinc-800/50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                {icon}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">{title}</h3>
            <p className="text-zinc-400 leading-relaxed">
                {description}
            </p>
        </div>
    );
}

function PricingFeature({ children, checked = false }) {
    return (
        <div className="flex items-start gap-3 text-sm">
            <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${checked ? 'bg-cyan-500/20 text-cyan-400' : 'bg-zinc-800 text-zinc-400'}`}>
                <Check className="w-3 h-3" />
            </div>
            <span className="text-zinc-300">{children}</span>
        </div>
    );
}
